import { Add, Delete } from '@mui/icons-material'
import { Button, Stack, useMediaQuery, useTheme } from '@mui/material'
import 'dayjs/locale/pl'
import { t } from 'i18next'
import { useCallback, useState } from 'react'
import { useSelector } from 'react-redux'
import { generatePath, useNavigate, useParams } from 'react-router-dom'
import defaultProps from '../../config/defaultProps'
import navigation from '../../config/navigation'
import { useNotification } from '../../store/redux/hooks'
import { useDeleteContractsBulkMutation, useGetEmployeeContractsQuery } from '../../store/redux/reducers/contractSlice'
import { getCurrentUser } from '../../store/redux/reducers/currentUserSlice'
import { RequestProps } from '../../store/types'
import { ContractLightDTO } from '../../store/types/models/Contract'
import { Column, Row } from '../../store/types/utils/DataTableUtils'
import { StyledButtonPrimary } from '../../styles/ButtonStyles'
import ConfirmationDialog from '../Dialog/ConfirmationDialog'
import SessionStorageKeys from '../../store/types/enums/SessionStorageKeys'
import DataTable from '../Table/DataTable'
import usePersistedPage from '../../store/utils/usePersistedPage'
import EmploymentExpandedRow from './EmploymentExpandedRow'
import EmploymentRow from './EmploymentRow'

type EmploymentManagementProps = {
  isMyProfile: boolean
  isManagementOrAdministrator: boolean
  isEditable: boolean
}

const EmploymentManagement = ({ isMyProfile, isManagementOrAdministrator, isEditable }: EmploymentManagementProps) => {
  const { id: userPathId } = useParams()
  const navigate = useNavigate()
  const currentUser = useSelector(getCurrentUser)
  const [requestProps, setRequestProps] = useState<RequestProps>({
    ...defaultProps,
    sortField: 'startDate',
    sortOrder: 1,
  })
  const { data: contracts } = useGetEmployeeContractsQuery(
    { id: isMyProfile ? (currentUser?.id as string) : (userPathId as string), requestProps },
    { skip: isMyProfile ? !currentUser?.id : !userPathId }
  )

  const userId = isMyProfile ? currentUser?.id : userPathId

  const theme = useTheme()
  const { displayNotification } = useNotification()

  const [deleteContractsBulk] = useDeleteContractsBulkMutation()

  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'))
  const [page, setPage] = usePersistedPage(SessionStorageKeys.EmploymentTablePage)
  const [isConfirmDeleteDialogOpen, setIsConfirmDeleteDialogOpen] = useState<boolean>(false)
  const [selectedData, setSelectedData] = useState<string[]>([])

  const [tableKey, setTableKey] = useState<number>(0)

  const columns: Column[] = [
    { id: 'type', label: t('page.employments.table.type') },
    { id: 'startDate', label: t('page.employments.table.startDate') },
    { id: 'endDate', label: t('page.employments.table.endDate') },
    { id: 'settlementMethod', label: t('page.employments.table.settlementType') },
    { id: 'employmentPercentage', label: t('page.employments.table.employmentPercentage') },
    { id: 'vacationPeriod', label: '' },
    { id: isManagementOrAdministrator ? 'action' : 'none', label: t('page.users.table.action') },
  ]

  const unsortableColumns: Column[] = [
    { id: 'type', label: t('page.employments.table.type') },
    { id: 'settlementMethod', label: t('page.employments.table.settlementType') },
    { id: 'employmentPercentage', label: t('page.employments.table.employmentPercentage') },
    { id: 'vacationPeriod', label: '' },
    { id: isManagementOrAdministrator ? 'action' : 'none', label: t('page.users.table.action') },
  ]

  const updateSelectedEmployments = useCallback(
    (selectedIds: string[]) => {
      setSelectedData(selectedIds)
    },
    [setSelectedData]
  )

  const handleConfirmDelete = async () => {
    setIsConfirmDeleteDialogOpen(false)
    try {
      if (selectedData.length === 0) {
        displayNotification({ message: t('page.employments.dialog.employmentsRemovedError'), type: 'error' })
        return
      }
      await deleteContractsBulk({ contractIds: selectedData }).unwrap()
      setSelectedData([])
      setTableKey(prev => prev + 1)
      displayNotification({ message: t('page.employments.dialog.employmentsRemoved'), type: 'success' })
    } catch (_) {
      displayNotification({ message: t('page.employments.dialog.employmentsRemovedError'), type: 'error' })
    }
  }

  const [openRowIds, setOpenRowIds] = useState<string[]>([])

  const toggleRow = (id: string) => {
    setOpenRowIds(prev => (prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id]))
  }

  const renderRow = (employment: Row) => {
    const isOpen = openRowIds.includes(employment.id)
    const contract = employment as ContractLightDTO
    const handleContractDeleted = (id: string) => {
      setSelectedData(prev => prev.filter(existingId => existingId !== id))
      setTableKey(prev => prev + 1)
    }

    return (
      <EmploymentRow
        isManagement={isManagementOrAdministrator}
        contract={contract}
        onToggleExpand={() => toggleRow(employment.id)}
        isOpen={isOpen}
        onContractDeleted={handleContractDeleted}
      />
    )
  }

  const renderExpandedRow = (employment: Row) => {
    const isOpen = openRowIds.includes(employment.id)
    const contract = employment as ContractLightDTO

    return <EmploymentExpandedRow contract={contract} isOpen={isOpen} isManagement={isManagementOrAdministrator} />
  }

  return (
    <>
      <Stack direction="column" gap={2} width={'100%'}>
        <Stack direction={isDownSm ? 'column' : 'row'} gap={2} sx={{ justifyContent: 'end', pt: isDownSm ? 2 : 0 }}>
          {isManagementOrAdministrator && (
            <>
              <Button
                startIcon={<Delete />}
                color="error"
                variant="contained"
                size="small"
                disabled={selectedData.length === 0}
                onClick={() => {
                  setIsConfirmDeleteDialogOpen(true)
                }}
              >
                {t('components.button.deleteSelectedContracts')}
              </Button>
              <StyledButtonPrimary
                variant="contained"
                startIcon={<Add />}
                size="small"
                fullWidth={isDownSm}
                onClick={() => navigate(generatePath(navigation.addContract, { userId }))}
              >
                {t('components.button.addEmployment')}
              </StyledButtonPrimary>
            </>
          )}
        </Stack>
        <DataTable
          key={tableKey}
          unsortableColumns={unsortableColumns}
          renderTileCheckbox={true}
          removeCheckboxes={!isManagementOrAdministrator}
          dense={false}
          singleSelection={false}
          columns={columns}
          data={contracts?.content || []}
          renderRow={renderRow}
          totalElements={contracts?.totalElements || 0}
          setRequestProps={setRequestProps}
          translationForNotFound={'page.employments.table.noEmploymentsFound'}
          page={page}
          setPage={setPage}
          isLastCellNormal={false}
          isExpandible={true}
          renderExpandedRow={renderExpandedRow}
          hoverOff={true}
          updateSelectedEmployments={updateSelectedEmployments}
          isRenderTileContent={false}
        />
      </Stack>

      <ConfirmationDialog
        open={isConfirmDeleteDialogOpen}
        setOpen={setIsConfirmDeleteDialogOpen}
        handleClose={() => {
          setIsConfirmDeleteDialogOpen(false)
        }}
        handleConfirm={handleConfirmDelete}
        title={t('page.employments.dialog.delete.multiple')}
      />
    </>
  )
}

export default EmploymentManagement
